import React from 'react';
import {
  Box,
  Typography,
  Grid,
  FormControl,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { Fab, Stack, Tooltip, Chip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BuildIcon from '@mui/icons-material/Build';
import CategoryIcon from '@mui/icons-material/Category';
import HandymanIcon from '@mui/icons-material/Handyman';
import EngineeringIcon from '@mui/icons-material/Engineering';
import MultipleSelectAutocomplete from './MultipleSelectAutocomplete';

const useStyles = makeStyles((theme) => ({
  // Ana Container Stilleri
  sectionContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    marginBottom: '16px',
  },
  sectionHeader: {
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '1.1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  sectionContent: {
    padding: '24px',
    backgroundColor: '#fffbeb',
  },
  // Item Card Stilleri
  itemCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #fde68a',
    padding: '20px',
    marginBottom: '16px',
    transition: 'all 0.2s ease',
    position: 'relative',
    '&:hover': {
      boxShadow: '0 4px 20px rgba(245, 158, 11, 0.15)',
      borderColor: '#f59e0b',
    },
  },
  itemNumber: {
    position: 'absolute',
    top: '-12px',
    left: '16px',
    backgroundColor: '#f59e0b',
    color: '#ffffff',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 700,
  },
  // Column Headers
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    paddingBottom: '8px',
    borderBottom: '2px solid #fde68a',
  },
  columnIcon: {
    color: '#d97706',
    fontSize: '20px',
  },
  columnTitle: {
    color: '#92400e',
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  // Add Button Stilleri
  addButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '16px 0',
    marginTop: '8px',
  },
  addButton: {
    backgroundColor: '#fef3c7',
    color: '#d97706',
    border: '2px dashed #f59e0b',
    borderRadius: '12px',
    padding: '16px 32px',
    fontWeight: 600,
    fontSize: '0.95rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&:hover': {
      backgroundColor: '#f59e0b',
      color: '#ffffff',
      borderStyle: 'solid',
    },
  },
  // Delete Button
  deleteButton: {
    backgroundColor: '#fef2f2 !important',
    color: '#dc2626 !important',
    transition: 'all 0.2s ease !important',
    '&:hover': {
      backgroundColor: '#dc2626 !important',
      color: '#ffffff !important',
    },
  },
  // Empty State
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px dashed #fde68a',
  },
  emptyIcon: {
    fontSize: '64px',
    color: '#fcd34d',
    marginBottom: '16px',
  },
  emptyTitle: {
    color: '#92400e',
    fontWeight: 500,
    fontSize: '1rem',
    marginBottom: '8px',
  },
  emptySubtitle: {
    color: '#b45309',
    fontSize: '0.875rem',
  },
  // Badge for item count
  itemBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#ffffff',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  // Chip Stilleri
  hizmetChip: {
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    fontWeight: 500,
    margin: '2px',
    '& .MuiChip-deleteIcon': {
      color: '#1d4ed8',
    },
  },
  iscilikChip: {
    backgroundColor: '#fef3c7',
    color: '#b45309',
    fontWeight: 500,
    margin: '2px',
    '& .MuiChip-deleteIcon': {
      color: '#b45309',
    },
  },
  // Summary Table Stilleri
  summaryContainer: {
    marginTop: '24px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
  },
  summaryHeader: {
    backgroundColor: '#f8fafc',
    padding: '12px 16px',
    borderBottom: '1px solid #e2e8f0',
  },
  summaryTitle: {
    color: '#475569',
    fontWeight: 600,
    fontSize: '0.95rem',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  tableContainer: {
    '& .MuiTableHead-root': {
      backgroundColor: '#f8fafc',
    },
    '& .MuiTableCell-head': {
      fontWeight: 600,
      color: '#475569',
      fontSize: '0.8rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      padding: '12px 16px',
      borderBottom: '2px solid #e2e8f0',
    },
    '& .MuiTableCell-body': {
      padding: '12px 16px',
      borderBottom: '1px solid #e2e8f0',
    },
    '& .MuiTableRow-root:hover': {
      backgroundColor: '#f8fafc',
    },
  },
  // Selected Items Display
  selectedItemsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    padding: '8px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    minHeight: '40px',
  },
  noItemsText: {
    color: '#94a3b8',
    fontStyle: 'italic',
    fontSize: '0.875rem',
    padding: '8px',
  },
}));

// Ana Hizmet ve İşçilik Ekleme Componenti
export function ServiceLaborSection({
  projectHizmetlerIscilikler,
  setProjectHizmetlerIscilikler,
  hizmetler,
  iscilikler,
  handleAddHizmetIscilik,
  handleHizmetlerChange,
  handleIsciliklerChange
}) {
  const classes = useStyles();
  const activeItems = projectHizmetlerIscilikler?.filter((x) => x.active === 1) || [];

  const handleDeleteItem = (itemId) => {
    const updatedItems = projectHizmetlerIscilikler.map((f) =>
      f.id === itemId ? { ...f, active: 0 } : f
    );
    setProjectHizmetlerIscilikler(updatedItems);
  };

  return (
    <Box className={classes.sectionContainer}>
      {/* Header */}
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle}>
          <EngineeringIcon />
          Hizmet ve İşçilik Ekle
        </Typography>
        {activeItems.length > 0 && (
          <Box className={classes.itemBadge}>
            {activeItems.length} kayıt
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box className={classes.sectionContent}>
        {activeItems.length === 0 ? (
          <Box className={classes.emptyState}>
            <HandymanIcon className={classes.emptyIcon} />
            <Typography className={classes.emptyTitle}>
              Henüz hizmet veya işçilik eklenmedi
            </Typography>
            <Typography className={classes.emptySubtitle}>
              Projenize hizmet ve işçilik eklemek için aşağıdaki butona tıklayın
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {activeItems.map((item, index) => (
              <Paper key={item.id} className={classes.itemCard} elevation={0}>
                {/* Item Number Badge */}
                <Box className={classes.itemNumber}>{index + 1}</Box>
                
                <Grid container spacing={3} alignItems="flex-start">
                  {/* Hizmetler Column */}
                  <Grid item xs={12} md={5}>
                    <Box className={classes.columnHeader}>
                      <CategoryIcon className={classes.columnIcon} />
                      <Typography className={classes.columnTitle}>
                        Hizmetler
                      </Typography>
                    </Box>
                    <FormControl fullWidth>
                      <MultipleSelectAutocomplete
                        id={`Hizmetler-${item.id}`}
                        data={hizmetler || []}
                        type="Hizmetler"
                        selectedValues={item.hizmetler || []}
                        onChange={(value) => handleHizmetlerChange(item.id, value)}
                      />
                    </FormControl>
                    {/* Seçilen Hizmetlerin Özeti */}
                    {item.hizmetler && item.hizmetler.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                          Seçili: {item.hizmetler.length} hizmet
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  {/* İşçilikler Column */}
                  <Grid item xs={12} md={5}>
                    <Box className={classes.columnHeader}>
                      <BuildIcon className={classes.columnIcon} />
                      <Typography className={classes.columnTitle}>
                        İşçilikler
                      </Typography>
                    </Box>
                    <FormControl fullWidth>
                      <MultipleSelectAutocomplete
                        id={`Iscilikler-${item.id}`}
                        data={iscilikler || []}
                        type="Iscilikler"
                        selectedValues={item.iscilikler || []}
                        onChange={(value) => handleIsciliklerChange(item.id, value)}
                      />
                    </FormControl>
                    {/* Seçilen İşçiliklerin Özeti */}
                    {item.iscilikler && item.iscilikler.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                          Seçili: {item.iscilikler.length} işçilik
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  {/* Delete Button */}
                  <Grid item xs={12} md={2} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Tooltip title="Kaydı Sil">
                      <Fab
                        size="small"
                        className={classes.deleteButton}
                        onClick={() => handleDeleteItem(item.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </Fab>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Hizmet/İşçilik Ekle Butonu */}
        <Box className={classes.addButtonContainer}>
          <Box
            className={classes.addButton}
            onClick={handleAddHizmetIscilik}
            role="button"
            tabIndex={0}
          >
            <AddCircleOutlineIcon />
            Yeni Hizmet ve İşçilik Ekle
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Read-Only Görünüm için Hizmet ve İşçilik Listesi
export function ServiceLaborReadOnly({
  projectHizmetlerIscilikler,
  hizmetler,
  iscilikler
}) {
  const classes = useStyles();
  const activeItems = projectHizmetlerIscilikler?.filter((x) => x.active === 1) || [];

  // Hizmet ve işçilik ID'lerini isimlere çevir
  const getHizmetName = (id) => {
    const found = hizmetler?.find((h) => h.Id === id);
    return found?.Dsc || `Hizmet #${id}`;
  };

  const getIscilikName = (id) => {
    const found = iscilikler?.find((i) => i.Id === id);
    return found?.Dsc || `İşçilik #${id}`;
  };

  return (
    <Box className={classes.sectionContainer}>
      {/* Header */}
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle}>
          <EngineeringIcon />
          Hizmet ve İşçilik Bilgileri
        </Typography>
        {activeItems.length > 0 && (
          <Box className={classes.itemBadge}>
            {activeItems.length} kayıt
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box className={classes.sectionContent}>
        {activeItems.length === 0 ? (
          <Box className={classes.emptyState}>
            <HandymanIcon className={classes.emptyIcon} />
            <Typography className={classes.emptyTitle}>
              Hizmet veya işçilik kaydı bulunmuyor
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: '12px' }} elevation={0}>
            <Table className={classes.tableContainer}>
              <TableHead>
                <TableRow>
                  <TableCell width={60}>#</TableCell>
                  <TableCell>Hizmetler</TableCell>
                  <TableCell>İşçilikler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeItems.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Chip
                        label={index + 1}
                        size="small"
                        sx={{
                          backgroundColor: '#f59e0b',
                          color: '#ffffff',
                          fontWeight: 600,
                          minWidth: '28px',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {item.hizmetler && item.hizmetler.length > 0 ? (
                          item.hizmetler.map((h) => (
                            <Chip
                              key={typeof h === 'object' ? h.Id : h}
                              label={typeof h === 'object' ? h.Dsc : getHizmetName(h)}
                              size="small"
                              className={classes.hizmetChip}
                            />
                          ))
                        ) : (
                          <Typography className={classes.noItemsText}>
                            -
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {item.iscilikler && item.iscilikler.length > 0 ? (
                          item.iscilikler.map((i) => (
                            <Chip
                              key={typeof i === 'object' ? i.Id : i}
                              label={typeof i === 'object' ? i.Dsc : getIscilikName(i)}
                              size="small"
                              className={classes.iscilikChip}
                            />
                          ))
                        ) : (
                          <Typography className={classes.noItemsText}>
                            -
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}

export default ServiceLaborSection;
